'use client';

import { RefreshEmailVerifyTokenDocument } from '@packages/gql/generated/shopping/RefreshEmailVerifyTokenDocument';
import { SendEmailVerifyTokenDocument } from '@packages/gql/generated/shopping/SendEmailVerifyTokenDocument';
import { useHashParameters } from '@packages/modules/src/hooks/useHashParameters/useHashParameters';
import {
  Breadcrumbs,
  BreadcrumbsList,
  BreadcrumbsItem,
  BreadcrumbsSeparator,
} from '@packages/shared/src/ui/Breadcrumbs/Breadcrumbs';
import { ButtonLink } from '@packages/shared/src/ui/Button/Button';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { Link } from '@packages/modules/src/Link/Link';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';

import type { NextPage } from 'next/types';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

/* GraphQL */ `
  mutation SendEmailVerifyToken($verifyEmailToken: String!) {
    verifyToken(token: $verifyEmailToken) {
      success
      isRefreshPossible
      emailAddress
    }
  }
`;

/* GraphQL */ `
  mutation RefreshEmailVerifyToken($locale: String!, $verifyEmailToken: String!) {
    refreshToken(locale: $locale, token: $verifyEmailToken) {
      success
    }
  }
`;

const messages = defineMessages({
  tokenSuccessHeadline: {
    id: 'VerifyEmail.Success.Headline',
    defaultMessage: 'Deine E-Mail Adresse ist bestätigt.',
  },
  tokenSuccessDescription: {
    id: 'VerifyEmail.Success.Description',
    defaultMessage:
      'Die Adresse {emailAddress} wurde erfolgreich bestätigt. Nun ist dein Konto sicherer gegenüber unbefugtem Zugriff.',
  },
  tokenSuccessInfo: {
    id: 'VerifyEmail.Success.Info',
    defaultMessage:
      'Entdecke jetzt unsere neuesten Angebote und  genieße ein sicheres und zuverlässiges Einkaufserlebnis.',
  },
  tokenSuccessButtonLabel: {
    id: 'VerifyEmail.Success.ButtonLabel',
    defaultMessage: 'Jetzt sicher einkaufen',
  },
  refreshFailHeadline: {
    id: 'VerifyEmail.GenerateFail.Headline',
    defaultMessage: 'Ups, das hat nicht geklappt',
  },
  refreshFailDescription: {
    id: 'VerifyEmail.GenerateFail.Description',
    defaultMessage:
      'Leider hat die E-Mail Verifizierung aufgrund eines technischen Fehlers nicht funktioniert. Bitte starte die E-Mail Verifizierung in deinem „Mein Konto“-Bereich neu.',
  },
  refreshFailButtonLabel: {
    id: 'VerifyEmail.GenerateFail.ButtonLabel',
    defaultMessage: 'Zum „Mein Konto“-Bereich',
  },
  tokenFailHeadline: {
    id: 'VerifyEmail.LinkFail.Headline',
    defaultMessage: 'Der Link ist ungültig',
  },
  tokenFailDescription: {
    id: 'VerifyEmail.LinkFail.Description',
    defaultMessage:
      'Leider ist der Link abgelaufen oder ungültig. Du kannst dir hier erneut einen Link an deine E-Mail Adresse schicken lassen. Dieser Link ist 7 Tage gültig.',
  },
  tokenFailButtonLabel: {
    id: 'VerifyEmail.LinkFail.ButtonLabel',
    defaultMessage: 'Verifizierungslink erneut zusenden',
  },
  breadCrumbMyAccount: {
    id: 'VerifyEmail.BreadCrumb.MyAccount',
    defaultMessage: 'Mein Konto',
  },
  breadCrumbVerifyEmail: {
    id: 'VerifyEmail.BreadCrumb.VerifyEmail',
    defaultMessage: 'Email verifizieren',
  },
  resentSuccessHeadline: {
    id: 'VerifyEmail.RetryInlineNotification.Headline',
    defaultMessage: 'Der Link ist ungültig',
  },
  resentSuccessDescription: {
    id: 'VerifyEmail.RetryInlineNotification.Description',
    defaultMessage:
      'Leider ist der Link abgelaufen oder ungültig. Du kannst dir hier erneut einen Link an deine E-Mail Adresse schicken lassen. Dieser Link ist 7 Tage gültig.',
  },
  resentSuccessButtonLabel: {
    id: 'VerifyEmail.RetryInlineNotification.ButtonLabel',
    defaultMessage: 'Lieblingsprodukte finden',
  },
  resentSuccessNotificationText: {
    id: 'VerifyEmail.RetryInlineNotification',
    defaultMessage:
      'Wir haben dir einen neuen Link an deine E-Mail Adresse geschickt. Falls du keine Mail bekommen hast, prüfe ggf. dein Spam-Postfach.',
  },
  resentSuccessSubText: {
    id: 'VerifyEmail.RetryInlineNotification',
    defaultMessage:
      'Manchmal kann es auch ein paar Minuten dauern, bis die Mail bei dir angezeigt wird, sieh dich doch so lange nach deinem neuen Lieblingsprodukt um.',
  },
});
/** This page is used to verify the email address of a user.
 * @state tokenSuccess -  the user is given success state
 * @state tokenFail - the user's token is invalid and allowed to refresh token.
 * @state refreshFail - the user cannot refreshToken and is directed to the myaccount page.
 * @state loading - the user is waiting for the api call to finish.
 */
const EmailVerificationPage: NextPage = () => {
  const { formatMessage, locale } = useIntl();
  const { token: verifyEmailToken } = useHashParameters();
  const url = useUrl();
  const {
    order: {
      paths,
      assets: {
        gifs: { success },
      },
    },
  } = useConfig();
  const { jwtToken } = useSession();
  const [verifyEmailState, setVerifyEmailState] = useState<
    'tokenSuccess' | 'tokenFail' | 'refreshFail' | 'loading' | 'resentSuccess'
  >('loading');
  const [showInlineNotification, setShowInlineNotification] = useState(true);
  const [verificationData, executeVerifyToken] = useAuthMutation(SendEmailVerifyTokenDocument);
  const [refreshTokenData, executeRefreshToken] = useAuthMutation(RefreshEmailVerifyTokenDocument);

  const {
    emailAddress,
    isRefreshPossible,
    success: verifySuccess,
  } = verificationData.data?.verifyToken ?? {};
  const { success: refreshSuccess } = refreshTokenData.data?.refreshToken ?? {};

  useEffect(() => {
    if (verifyEmailToken && jwtToken) {
      // if we have a token, we need to verify it
      /* jwt is required in the header to verify the token on the backend api call
      at first jwt will be undefined and we need to wait for the session context to be loaded. the problem is that the session context is lazy-loaded at runtime. so we created jwt as a dependency to this effect to trigger a rerender once the session context is loaded */
      setVerifyEmailState('loading');
      executeVerifyToken({ verifyEmailToken });
      return;
    }
    if (verifyEmailToken) {
      // we have the token from hash parameters, but we are waiting for the jwt to be loaded
      setVerifyEmailState('loading');
    } else {
      // if we don't have a token nor a jwt, we can't fire the verify token call, so we need to redirect to myaccount
      setVerifyEmailState('refreshFail');
    }
  }, [jwtToken, executeVerifyToken, verifyEmailToken]);

  useEffect(() => {
    if (refreshSuccess) {
      // if the token refresh succeeds, we need to display the resent success message
      setVerifyEmailState('resentSuccess');
      return;
    }
    if (verifySuccess) {
      // if the token verification succeeds, we need to display the success message
      setVerifyEmailState('tokenSuccess');
      return;
    }
    if (isRefreshPossible && typeof refreshSuccess === 'undefined') {
      // first api returns isRefreshPossible true
      // because refreshSuccess is undefined, we haven't made the second api call yet
      // if we can refresh the token, we need to display the refresh button
      setVerifyEmailState('tokenFail');
      return;
    }
    if (verifySuccess === false) {
      // fallback to full failure which will display button redirect to myaccount
      setVerifyEmailState('refreshFail');
    }
  }, [verifySuccess, refreshSuccess, isRefreshPossible]);

  const handleRefreshToken = () => {
    // this is our second api call to attempt to refresh the token
    if (verifyEmailToken && isRefreshPossible) {
      setVerifyEmailState('loading');
      executeRefreshToken({ locale, verifyEmailToken });
    }
  };

  const urlRedirect =
    verifyEmailState === 'tokenSuccess' || verifyEmailState === 'resentSuccess'
      ? '/'
      : getLocalizedPath(paths.myAccountOverview, locale);
  return (
    <div className="px-3 py-6">
      <Breadcrumbs className="self-start">
        <BreadcrumbsList>
          <BreadcrumbsItem>
            <Link
              className="text-inherit hover:underline"
              href={getLocalizedPath(paths.myAccountOverview, locale)}
            >
              {formatMessage(messages.breadCrumbMyAccount)}
            </Link>
          </BreadcrumbsItem>
          <BreadcrumbsSeparator />
          <BreadcrumbsItem>
            <Link className="text-inherit hover:underline" href={url}>
              {formatMessage(messages.breadCrumbVerifyEmail)}
            </Link>
          </BreadcrumbsItem>
        </BreadcrumbsList>
      </Breadcrumbs>
      <div className="flex max-w-96 flex-col items-center justify-start self-center pb-12">
        {verifyEmailState === 'tokenSuccess' && (
          <Image unoptimized src={success.url} alt="success image" height={128} width={128} />
        )}
        {(verifyEmailState === 'refreshFail' ||
          verifyEmailState === 'tokenFail' ||
          verifyEmailState === 'resentSuccess') && (
          <span className="icon-emp-error-circle text-color-error size-[6rem]" aria-hidden="true" />
        )}
        {verifyEmailState === 'loading' && <Skeleton className="size- rounded-full" />}
        <div className="mt-2 mb-6 w-full text-center font-semibold">
          <h4 className="text-size-headline-xl">
            {verifyEmailState !== 'loading' &&
              formatMessage(messages[`${verifyEmailState}Headline` as keyof typeof messages])}
            {verifyEmailState === 'loading' && <Skeleton className="h-[1.875rem] w-full" />}
          </h4>
        </div>
        <div className="text-size-body-sm mb-2 w-full text-center">
          {verifyEmailState !== 'loading' &&
            formatMessage(messages[`${verifyEmailState}Description` as keyof typeof messages], {
              emailAddress,
            })}
          {verifyEmailState === 'loading' && (
            <>
              <Skeleton className="mb-1 h-[0.938rem] w-full rounded" />
              <Skeleton className="mb-1 h-[0.938rem] w-full" />
              <Skeleton className="mb-1 h-[0.938rem] w-full" />
            </>
          )}
        </div>
        {verifyEmailState === 'tokenSuccess' && (
          <p className="text-size-body-sm text-center">
            {formatMessage(messages[`${verifyEmailState}Info` as keyof typeof messages])}
          </p>
        )}
        {verifyEmailState === 'resentSuccess' && (
          <>
            {showInlineNotification && (
              <Notification className="mt-3" onClose={() => setShowInlineNotification(false)}>
                {formatMessage(messages.resentSuccessNotificationText)}
              </Notification>
            )}
            <p className="text-size-body-sm mt-3 -mb-5 text-center">
              {formatMessage(messages.resentSuccessSubText)}
            </p>
          </>
        )}
        {verifyEmailState !== 'loading' && (
          <ButtonLink
            className="mt-8 py-2"
            LinkComponent={Link}
            color="primary"
            href={!verifyEmailState.includes('tokenFail') ? urlRedirect : undefined}
            onClick={handleRefreshToken}
            role="button"
          >
            {formatMessage(messages[`${verifyEmailState}ButtonLabel` as keyof typeof messages])}
            {verifyEmailState === 'tokenFail' && (
              <span className="icon-emp-paperplane ml-2 size-6" aria-hidden="true" />
            )}
          </ButtonLink>
        )}
        {verifyEmailState === 'loading' && <CircularProgress className="mt-8" />}
      </div>
    </div>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default EmailVerificationPage;
